import { useState, useCallback, useEffect, useRef } from 'react';
import { PostgrestError } from '@supabase/supabase-js';

/** Custom event name dispatched by useCacheStore when cache is written. */
export const CACHE_UPDATED_EVENT = 'bookmark-cache-updated';

interface CacheFirstQueryOptions<T> {
  /** Read full array from cache. Return null if cache is empty. */
  cacheReader: () => T[] | null;
  /** Filter cached data for this specific query. */
  cacheFilter: (items: T[]) => T[];
  /** Fetch from network. Return { data, error }. */
  networkFetch: () => Promise<{ data: T[] | null; error: PostgrestError | null }>;
  /** Whether this query should execute at all. */
  enabled?: boolean;
  /** Change this value to re-run the query (e.g., the categoryId or parentId). */
  queryKey: string | null;
}

interface CacheFirstQueryReturn<T> {
  data: T[] | undefined;
  loading: boolean;
  error: PostgrestError | null;
  refetch: () => Promise<void>;
}

/**
 * Generic cache-first data hook.
 *
 * Strategy:
 * 1. Check cache → if data exists, filter and return immediately (no loading state)
 * 2. If cache is empty → hit network, show loading, return result
 * 3. refetch() always hits network (for forced refresh)
 * 4. Re-runs when queryKey, enabled, or cache version changes
 */
function useCacheFirstQuery<T>(
  options: CacheFirstQueryOptions<T>
): CacheFirstQueryReturn<T> {
  const { enabled = true, queryKey } = options;
  const [data, setData] = useState<T[] | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<PostgrestError | null>(null);
  const [cacheVersion, setCacheVersion] = useState(0);

  // Use refs to avoid dependency loops — these functions may change identity
  // on every render but their behavior is stable
  const cacheReaderRef = useRef(options.cacheReader);
  const cacheFilterRef = useRef(options.cacheFilter);
  const networkFetchRef = useRef(options.networkFetch);

  cacheReaderRef.current = options.cacheReader;
  cacheFilterRef.current = options.cacheFilter;
  networkFetchRef.current = options.networkFetch;

  // Listen for cache update events
  useEffect(() => {
    const handleCacheUpdate = () => {
      setCacheVersion((v) => v + 1);
    };
    window.addEventListener(CACHE_UPDATED_EVENT, handleCacheUpdate);
    return () => {
      window.removeEventListener(CACHE_UPDATED_EVENT, handleCacheUpdate);
    };
  }, []);

  const fetchFromNetwork = useCallback(async () => {
    setLoading(true);
    setError(null);

    const { data: result, error: queryError } = await networkFetchRef.current();

    if (queryError) {
      setError(queryError);
      setData(undefined);
    } else {
      setData(result ?? []);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setData(undefined);
      setLoading(false);
      setError(null);
      return;
    }

    // Try cache first
    const cached = cacheReaderRef.current();
    if (cached !== null) {
      const filtered = cacheFilterRef.current(cached);
      setData(filtered);
      setLoading(false);
      setError(null);
      return;
    }

    // Cache miss — hit network
    fetchFromNetwork();
  }, [enabled, queryKey, cacheVersion, fetchFromNetwork]);

  return { data, loading, error, refetch: fetchFromNetwork };
}

export { useCacheFirstQuery };
export type { CacheFirstQueryOptions, CacheFirstQueryReturn };
